import { useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useSettings } from '../context/SettingsContext';

/**
 * Hook to get localized clinic settings.
 * Returns `s(field)` — looks up `fieldEn` for English, `field` for Ukrainian.
 * Returns `phoneHref` — sanitized phone number for tel: links.
 *
 * Usage:
 *   const { settings, s, phoneHref } = useLocalizedSettings();
 *   s('address')       → settings.addressEn (EN) or settings.address (UK)
 *   settings.phone     → raw setting value (not localized)
 *   phoneHref          → "tel:+380969471473"
 */
export const useLocalizedSettings = () => {
    const { language } = useLanguage();
    const { settings } = useSettings();

    const s = useCallback((field) => {
        if (language === 'en') {
            return settings[field + 'En'] || settings[field] || '';
        }
        return settings[field] || '';
    }, [language, settings]);

    /** Sanitized phone for tel: links — strips everything except digits and '+' */
    const phoneHref = useMemo(
        () => `tel:${(settings.phone || '').replace(/[^\d+]/g, '')}`,
        [settings.phone]
    );

    return { settings, s, phoneHref };
};

